import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PageHeader } from '@/components/layout/PageHeader';
import { ScrollTable } from '@/components/layout/ScrollTable';
import { apiFetch, ApiError } from '@/lib/api';
import { useT } from '@/lib/i18n';

interface AuditRow {
  _id: string;
  actor: string;
  action: string;
  target_tenant_id?: string;
  at: string;
  payload?: Record<string, unknown>;
}

interface AuditResp {
  audit: AuditRow[];
}

export function PlatformAudit() {
  const t = useT();
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await apiFetch<AuditResp>('/platform/audit?limit=100');
        setRows(r.audit ?? []);
      } catch (e) {
        setErr(e instanceof ApiError ? e.message : 'failed');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('superadmin.audit.title')}
        subtitle={t('superadmin.audit.subtitle')}
      />
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 text-sm text-muted-foreground">
              {t('auth.loading')}
            </div>
          ) : err ? (
            <div className="p-6 text-sm text-destructive">{err}</div>
          ) : rows.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground">
              {t('superadmin.audit.empty')}
            </div>
          ) : (
            <ScrollTable>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('superadmin.audit.colAt')}</TableHead>
                    <TableHead>{t('superadmin.audit.colActor')}</TableHead>
                    <TableHead>{t('superadmin.audit.colAction')}</TableHead>
                    <TableHead>{t('superadmin.audit.colTarget')}</TableHead>
                    <TableHead className="hidden lg:table-cell">
                      {t('superadmin.audit.colPayload')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map(r => (
                    <TableRow key={r._id}>
                      <TableCell className="font-mono text-xs whitespace-nowrap">
                        {r.at?.replace('T', ' ').slice(0, 19)}
                      </TableCell>
                      <TableCell className="text-xs">{r.actor}</TableCell>
                      <TableCell>
                        <span className="font-mono text-xs">{r.action}</span>
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {r.target_tenant_id || '—'}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell text-xs text-muted-foreground max-w-md truncate">
                        {r.payload ? JSON.stringify(r.payload) : ''}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollTable>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
